import { SvgIconProps } from "@components/SvgIcon";
import { Box, Divider, Stack, Tooltip, useTheme } from "@mui/material";
import { isArray } from "lodash";
import {
  DescriptionStyled,
  ListItemIconStyled,
  ListItemTextStyled,
  MenuItemStyled,
  MenuStyled,
} from "./DropDownMenu.styled";
import { FunctionComponent, ReactNode, RefObject, SVGProps } from "react";
import CheckIcon from "@assets/icons_new/check.svg?react";

export interface MenuItemIf {
  text: string;
  description?: string;
  onClick: () => void;
  Icon?: (props: SvgIconProps) => JSX.Element;
  IconNew?: FunctionComponent<
    SVGProps<SVGSVGElement> & {
      title?: string;
    }
  >;
  disabled?: boolean;
  tooltip?: string;
  ref?: RefObject<HTMLLIElement> | null;
  checked?: boolean;
  endAdornment?: ReactNode;
}
interface DropDownMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  options: MenuItemIf[] | MenuItemIf[][];
  extraChildren?: ReactNode;
  menuMinWidth?: number;
}

export const DropDownMenu = ({
  anchorEl,
  onClose,
  options,
  extraChildren,
  menuMinWidth,
}: DropDownMenuProps) => {
  const {
    colors: { neutral },
  } = useTheme();
  const closeMenu = (fn: () => void) => () => {
    onClose();
    fn();
  };

  const renderOptions = () => {
    const elements: JSX.Element[] = [];

    options.forEach((option, optionIdx) => {
      if (isArray(option)) {
        if (optionIdx > 0) {
          elements.push(<Divider key={`divider-${optionIdx}`} />);
        }
        option.forEach((o, oIdx) => {
          elements.push(
            <Tooltip
              title={o.tooltip}
              key={`option-${optionIdx}-${oIdx}`}
              disableHoverListener={!o.tooltip}
            >
              <div>
                <MenuItemStyled
                  onClick={closeMenu(o.onClick)}
                  disabled={o.disabled}
                  ref={o.ref}
                  minWidth={menuMinWidth}
                >
                  {o.Icon && (
                    <ListItemIconStyled>
                      <o.Icon fill={neutral.dark[700]} />
                    </ListItemIconStyled>
                  )}
                  {o.IconNew && (
                    <ListItemIconStyled>
                      <o.IconNew color={neutral.dark[700]} fontSize={24} />
                    </ListItemIconStyled>
                  )}
                  <Box className="flex flex-col">
                    <ListItemTextStyled>{o.text}</ListItemTextStyled>
                    {o.description && (
                      <DescriptionStyled>{o.description}</DescriptionStyled>
                    )}
                  </Box>
                  {o.endAdornment}
                  {o.checked && (
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      width="100%"
                    >
                      <CheckIcon color={neutral.dark[700]} fontSize={24} />
                    </Stack>
                  )}
                </MenuItemStyled>
              </div>
            </Tooltip>,
          );
        });
      } else {
        elements.push(
          <Tooltip
            title={option.tooltip}
            key={`option-${optionIdx}`}
            disableHoverListener={!option.tooltip}
          >
            <div>
              <MenuItemStyled
                onClick={closeMenu(option.onClick)}
                disabled={option.disabled}
                ref={option.ref}
                minWidth={menuMinWidth}
              >
                {option.Icon && (
                  <ListItemIconStyled>
                    <option.Icon fill={neutral.dark[700]} />
                  </ListItemIconStyled>
                )}
                {option.IconNew && (
                  <ListItemIconStyled>
                    <option.IconNew color={neutral.dark[700]} fontSize={24} />
                  </ListItemIconStyled>
                )}
                <Box className="flex flex-col">
                  <ListItemTextStyled>{option.text}</ListItemTextStyled>
                  {option.description && (
                    <DescriptionStyled>{option.description}</DescriptionStyled>
                  )}
                </Box>
                {option.endAdornment}
                {option.checked && (
                  <Stack direction="row" justifyContent="flex-end" width="100%">
                    <CheckIcon color={neutral.dark[700]} fontSize={24} />
                  </Stack>
                )}
              </MenuItemStyled>
            </div>
          </Tooltip>,
        );
      }
    });

    return elements;
  };

  return (
    <MenuStyled open={Boolean(anchorEl)} onClose={onClose} anchorEl={anchorEl}>
      {extraChildren}
      {renderOptions()}
    </MenuStyled>
  );
};
